// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * Zooming and panning the sheet.
 *
 * A site plan is read at two distances. From across the room you want the
 * whole lot: does the laydown clear the fire lane, is there room to turn a
 * truck. Up close you want one corner: where exactly the gate sits against
 * the property line, whether the trailer misses the tree. A drawing fixed at
 * one size serves neither, and a plan you cannot zoom is one people export and
 * open in something else.
 *
 * The view is a scale and an offset applied to the sheet and its annotations
 * together, so the marks stay registered to the drawing no matter how far in
 * you are. Nothing here changes what anything measures: the scale of the plan
 * is a property of the drawing, zoom is a property of looking at it.
 */

export interface ViewTransform {
  scale: number;
  /** Offset in screen pixels, applied after the scale. */
  tx: number;
  ty: number;
}

/** The whole sheet, square on, which is where the view starts and resets to. */
export const FIT: ViewTransform = { scale: 1, tx: 0, ty: 0 };

export const MIN_SCALE = 0.25;
export const MAX_SCALE = 8;

/** How much one notch of the wheel changes the scale. */
export const WHEEL_STEP = 1.12;

export function clampScale(scale: number, min = MIN_SCALE, max = MAX_SCALE): number {
  // NaN has no sensible answer and falls back to fit; an infinity does have
  // one, and clamping it is better than silently resetting the view.
  if (Number.isNaN(scale)) return 1;
  return Math.max(min, Math.min(max, scale));
}

/**
 * Zoomed about a point, so whatever is under the cursor stays under it.
 *
 * Zooming about the middle instead is the thing that makes a drawing feel
 * like it is fighting you: the detail you are pointing at slides away exactly
 * when you are trying to look at it.
 */
export function zoomAt(
  view: ViewTransform, factor: number, cx: number, cy: number,
  min = MIN_SCALE, max = MAX_SCALE,
): ViewTransform {
  const next = clampScale(view.scale * factor, min, max);
  if (next === view.scale) return view;
  const k = next / view.scale;
  return {
    scale: next,
    tx: cx - (cx - view.tx) * k,
    ty: cy - (cy - view.ty) * k,
  };
}

/** Moved by a drag, in screen pixels. */
export function panBy(view: ViewTransform, dx: number, dy: number): ViewTransform {
  if (!dx && !dy) return view;
  return { ...view, tx: view.tx + dx, ty: view.ty + dy };
}

/** Whether the view is showing the whole sheet, square on. */
export function isFitted(view: ViewTransform): boolean {
  return Math.abs(view.scale - 1) < 0.001
    && Math.abs(view.tx) < 0.5 && Math.abs(view.ty) < 0.5;
}

/** A press, for working out whether the next one is a double. */
export interface Press { t: number; x: number; y: number }

/** How long after a press a second one still counts as a double. */
export const DOUBLE_MS = 450;
/** How far it may move and still be the same gesture. */
export const DOUBLE_SLOP = 6;

/**
 * Whether this press completes a double click.
 *
 * Counted here rather than read from the event, because a pointer event does
 * not carry one: `detail` is zero on pointerdown by specification, and the
 * click count lives on the mouse events. Checking it there looks right, tests
 * green if the test supplies the number itself, and never fires for a real
 * mouse.
 *
 * The second press has to land near the first as well as soon after it, or a
 * drag of the sheet followed by another middle click would read as a double
 * and throw the view away.
 */
export function isDoublePress(prev: Press | null, now: Press,
  withinMs = DOUBLE_MS, withinPx = DOUBLE_SLOP): boolean {
  if (!prev) return false;
  if (now.t - prev.t > withinMs || now.t < prev.t) return false;
  return Math.hypot(now.x - prev.x, now.y - prev.y) <= withinPx;
}

/** The transform as CSS, for the element holding the sheet and its marks. */
export function toCss(view: ViewTransform): string {
  if (isFitted(view)) return "none";
  return `translate(${view.tx}px, ${view.ty}px) scale(${view.scale})`;
}

/** A readable zoom level: 100% at fit, rounded to something worth reading. */
export function formatZoom(view: ViewTransform): string {
  return `${Math.round(view.scale * 100)}%`;
}

/**
 * How far one wheel event should scale.
 *
 * Trackpads report many small deltas and a mouse reports a few large ones, so
 * the step follows the delta rather than being fixed; otherwise a trackpad
 * pinch crawls and a mouse notch jumps half the range.
 */
export function wheelFactor(deltaY: number, step = WHEEL_STEP): number {
  if (!Number.isFinite(deltaY) || deltaY === 0) return 1;
  // Clamped so one violent scroll cannot cross the whole range at once.
  const notches = Math.max(-3, Math.min(3, -deltaY / 100));
  return Math.pow(step, notches);
}
